import type { LocationIntelPointDto } from '@pams/shared';
import { Button, type GetRef, Select } from 'antd';
import { Expand, LocateFixed } from 'lucide-react';
import type { GeoJSONSource, Map as MapboxMap, PaintSpecification } from 'mapbox-gl';
import { useEffect, useRef, useState } from 'react';
import { LOCATION_INTEL_COPY } from '../copy';
import {
  BASEMAPS,
  BASEMAP_STYLE,
  CENTRAL_KOLKATA,
  CENTRAL_KOLKATA_ZOOM,
  CLUSTER_LAYER_ID,
  INTERACTIVE_LAYER_IDS,
  POINT_LAYER_ID,
  POINTS_SOURCE_ID,
  boundsOf,
  isSourceError,
  isTokenRefusal,
  mapErrorLine,
  pointLayers,
  pointsData,
  pointsSource,
  type Basemap,
  type MapColours,
} from '../mapSpec';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [LOCATION-INTEL] THE MAP — Mapbox GL, loaded on demand, drawing the points the page has read.
 *
 *  ⚠️ [LOCATION-INTEL.1] THE MAPBOX CONTAINER IS SIZED IN NORMAL FLOW (`size-full`), NEVER `absolute inset-0` —
 *  AND THAT WAS THE BLANK MAP. MEASURED in Chromium with the built CSS and the real 3.32.0 library: Mapbox adds
 *  `mapboxgl-map` to the element it is given, and `mapbox-gl.css` — loaded with the lazy chunk, OUTSIDE every
 *  cascade layer — sets `.mapboxgl-map { position: relative; overflow: hidden }`. Unlayered CSS beats every layered
 *  rule, so it overrode our `absolute` utility: the container fell from 698 px to 0 px tall, the canvas was clipped
 *  by Mapbox's own `overflow: hidden`, and the map drew NOTHING, with no error anywhere. A container that takes its
 *  size from its own `height: 100%` has no position for the vendor rule to undo. Do not "tidy" it back.
 *
 *  ⚠️ [LOCATION-INTEL.1] AND MAPBOX RE-MEASURES THAT CONTAINER ONLY ON A WINDOW RESIZE. 3.32.0 listens for the
 *  window's `resize`, `orientationchange` and `fullscreenchange` and nothing else, so a ResizeObserver here calls
 *  `map.resize()`. MEASURED without it: collapsing the sidebar at 1366 × 768 grew the container from 1060 to 1260 px
 *  while the canvas stayed 1060, a 200 px blank band; and a header that wraps when "Updated" arrives left the
 *  canvas 40 px taller than its box, clipped. The canvas is `position: absolute`, so resizing it cannot resize the
 *  observed box — no observer loop.
 *
 *  ⚠️ THE LIBRARY ARRIVES THROUGH `import('../mapbox')` IN AN EFFECT, never a static import: the page is in the
 *  route table, which every route test and the app's first paint import. See `mapbox.ts`.
 *
 *  ⚠️ THE TOKEN IS PASSED TO THIS MAP (`accessToken`), NEVER SET ON THE LIBRARY'S GLOBAL, and Mapbox's performance
 *  metrics are off. ⚠️ [LOCATION-INTEL.1] THAT IS NOT "NOTHING MORE". 3.32.0 still sends events.mapbox.com a
 *  `map.load` event and api.mapbox.com a map-session request on every load (its terms require both), a `style.load`
 *  event naming the basemap on every style load — the first and each basemap change — and, once a day, an
 *  `appUserTurnstile` event. It keeps an anonymous id, renewed every 24 hours, in `localStorage`, and SENDS it as
 *  `userId` with each `map.load` and `appUserTurnstile`; three keys hold it all (`mapbox.eventData.uuid:`,
 *  `mapbox.eventData.uuidTimestamp:`, and `mapbox.eventData:` for the turnstile's last send). No option of this map
 *  turns any of them off. What Mapbox never receives is the officers' points: the source is drawn in this browser.
 *
 *  ⚠️ [LOCATION-INTEL.1] IT OPENS ON CENTRAL KOLKATA AND NEVER MOVES ITSELF. The user's instruction; the old fit to
 *  every point's box is now a button ("All officers"), so a refresh never jumps the view and one phone far outside
 *  the city never opens the map on the whole state. "Central Kolkata" brings the view back, bearing and pitch reset.
 *
 *  ⚠️ A NEW BASEMAP RELOADS THE STYLE, AND `style.load` PUTS THE POINTS BACK. `setStyle` removes every source and
 *  layer the page added, so the one `style.load` handler adds them from the LATEST points and colours (refs) on the
 *  first load and on every reload alike — one code path, not two.
 *
 *  ⚠️ [LOCATION-INTEL.1] A STYLE THAT DOES NOT LOAD SAYS SO ON THE SCREEN. 3.32.0 fires one `error` for a failed style
 *  request (a blocked host, a 404, a 5xx) and never `style.load`, and it never retries — so an error that arrives
 *  while a style load is in flight, and belongs to no tile or source, is the style's own. It used to leave "Loading
 *  the map" up for ever (or a blank map after a basemap switch), with the reason only in the console. The overlay
 *  offers Try again, and the view controls stay above it, so another basemap can be chosen.
 *
 *  ⚠️ [LOCATION-INTEL.1] BEHIND A FAILURE OVERLAY THE MAP IS `inert` — never behind "Loading the map", which keeps the
 *  map usable. The canvas and Mapbox's own controls are focusable, and a keyboard otherwise walked stops it could not
 *  see before reaching Try again: five while no style has loaded, and once one has, the attribution's links as well
 *  (eight at 1366 × 768). Try again hands focus to the Map style picker before its button goes, never to `<body>`,
 *  and the picker is outside the inert container. Only a retry refused as a token takes the picker away, because no
 *  control is left on the map; focus then falls to the page while the overlay's alert is read out.
 *
 *  ⚠️ EVERY ZOOM IS MAPBOX'S OWN: the +/− buttons and the compass (which also shows and resets pitch), the scale,
 *  scroll, double-click, box (shift-drag), pinch and keyboard zoom, all defaults, over Mapbox's default range of
 *  −2 to 22. No fullscreen control: a fullscreen MAP ELEMENT would hide the officer card and the map key, which are
 *  dialogs rendered outside it — the page already gives the map the whole content area.
 *
 *  ⚠️ A DOT IS SELECTED BY POINTER ONLY. A canvas has no element per dot for a keyboard to reach; the map pans and
 *  zooms from the keyboard (Mapbox's handlers on the focused canvas), and the page states every count in words in
 *  the Map key and to screen readers beside the map. A stated gap.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

type Failure = 'load' | 'webgl' | 'token' | 'style';

type FeatureLike = {
  properties?: Record<string, unknown> | null;
  geometry?: { type: string; coordinates?: unknown };
};

const copy = LOCATION_INTEL_COPY;

const FAILURE_WORDS: Record<Failure, string> = {
  load: copy.mapFailedLoad,
  webgl: copy.mapFailedWebGl,
  token: copy.mapFailedToken,
  style: copy.mapFailedStyle,
};

const BASEMAP_OPTIONS = BASEMAPS.map((value) => ({ value, label: copy.basemap[value] }));

/*
 * `diff: false`: a full reload, so `style.load` fires and the points come back.
 * ⚠️ THE CAST IS FOR A TYPE THAT OVER-REQUIRES: 3.32.0's `SetStyleOptions` marks the two local font
 * families required, while `setStyle` merges what it is given OVER the map's own values — so passing
 * `undefined` for them would erase the map's defaults rather than keep them.
 */
const RELOAD = { diff: false } as Parameters<MapboxMap['setStyle']>[1];

export const LocationMap = ({
  token,
  points,
  colours,
  basemap,
  notice,
  onBasemapChange,
  onSelect,
}: {
  token: string;
  points: readonly LocationIntelPointDto[];
  colours: MapColours;
  basemap: Basemap;
  /** [LOCATION-INTEL.1] One line over the map for a state a viewer must not mistake for "nothing showing" — or `null`. */
  notice: string | null;
  onBasemapChange: (next: Basemap) => void;
  onSelect: (officerId: string) => void;
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  /* The latest props, read by handlers that Mapbox holds — a stale closure would draw old points. */
  const pointsRef = useRef(points);
  const coloursRef = useRef(colours);
  const onSelectRef = useRef(onSelect);
  const basemapRef = useRef(basemap);
  /* The basemap the map last LOADED, so a change of prop reloads once and a re-render reloads nothing. */
  const styledRef = useRef<Basemap | null>(null);
  /* Whether a style request is in flight — from creation, and from every `setStyle` until its `style.load`. */
  const styleLoadingRef = useRef(false);
  /* Where focus goes when Try again's button leaves — see the header. */
  const basemapSelectRef = useRef<GetRef<typeof Select>>(null);
  const [ready, setReady] = useState(false);
  const [failure, setFailure] = useState<Failure | null>(null);

  useEffect(() => {
    pointsRef.current = points;
    coloursRef.current = colours;
    onSelectRef.current = onSelect;
    basemapRef.current = basemap;
  });

  useEffect(() => {
    let cancelled = false;
    let created: MapboxMap | null = null;
    let observer: ResizeObserver | null = null;

    import('../mapbox')
      .then(({ default: mapboxgl }) => {
        const container = containerRef.current;
        if (cancelled || container === null) return;
        let map: MapboxMap;
        try {
          map = new mapboxgl.Map({
            container,
            accessToken: token,
            style: BASEMAP_STYLE[basemapRef.current],
            center: CENTRAL_KOLKATA,
            zoom: CENTRAL_KOLKATA_ZOOM,
            performanceMetricsCollection: false,
            respectPrefersReducedMotion: true,
          });
        } catch {
          setFailure('webgl');
          return;
        }
        created = map;
        mapRef.current = map;
        styledRef.current = basemapRef.current;
        styleLoadingRef.current = true;
        map.addControl(new mapboxgl.NavigationControl({ visualizePitch: true }), 'top-right');
        map.addControl(new mapboxgl.ScaleControl({ unit: 'metric' }), 'bottom-left');

        /* See the header: Mapbox re-measures only on a window resize. jsdom has no ResizeObserver, hence the guard. */
        if (typeof ResizeObserver !== 'undefined') {
          observer = new ResizeObserver(() => {
            map.resize();
          });
          observer.observe(container);
        }

        map.on('style.load', () => {
          styleLoadingRef.current = false;
          map.addSource(POINTS_SOURCE_ID, pointsSource(pointsRef.current));
          for (const layer of pointLayers(coloursRef.current)) map.addLayer(layer);
          setReady(true);
          setFailure((current) => (current === 'style' ? null : current));
        });

        /*
         * ⚠️ [LOCATION-INTEL.1] EVERY MAPBOX ERROR REACHES THE CONSOLE; A REFUSED TOKEN AND A STYLE THAT WILL NOT LOAD
         * ALSO REACH THE SCREEN. Mapbox logs an error itself only while NOTHING listens for one, so this listener used
         * to SILENCE every failure but the token's. Its token is masked, in the message as well as the address.
         * ⚠️ A 404 ON A TILE OR A FONT IS NOT AN ERROR EVENT: 3.32.0 turns it into a `data` event (`sourceDataType:
         * 'error'`), so it never reaches this listener — the browser's own "Failed to load resource: 404" line, with
         * the address, is what shows it. Both faces the cluster labels name answer 200 from Mapbox (checked).
         */
        map.on('error', (event) => {
          console.error(mapErrorLine(event.error));
          if (isTokenRefusal(event.error)) setFailure('token');
          else if (styleLoadingRef.current && !isSourceError(event)) setFailure('style');
        });

        map.on('click', CLUSTER_LAYER_ID, (event) => {
          const feature = (event.features as FeatureLike[] | undefined)?.[0];
          const clusterId = feature?.properties?.cluster_id;
          const centre = feature?.geometry?.coordinates;
          const source = map.getSource<GeoJSONSource>(POINTS_SOURCE_ID);
          if (typeof clusterId !== 'number' || !Array.isArray(centre) || source === undefined) return;
          source.getClusterExpansionZoom(clusterId, (error, zoom) => {
            if (error || zoom == null) return;
            map.easeTo({ center: centre as [number, number], zoom });
          });
        });

        map.on('click', POINT_LAYER_ID, (event) => {
          const id = (event.features as FeatureLike[] | undefined)?.[0]?.properties?.id;
          if (typeof id === 'string') onSelectRef.current(id);
        });

        /*
         * ⚠️ ONE enter/leave pair over BOTH layers, never a pair per layer: per layer, moving from a dot straight onto
         * a cluster ran the cluster's enter and then the dot's leave, and left the plain cursor over a clickable cluster.
         */
        map.on('mouseenter', [...INTERACTIVE_LAYER_IDS], () => {
          map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', [...INTERACTIVE_LAYER_IDS], () => {
          map.getCanvas().style.cursor = '';
        });
      })
      .catch(() => {
        if (!cancelled) setFailure('load');
      });

    /* ⚠️ A NEW TOKEN IS A NEW COMPONENT — the page keys this map on it — so the cleanup only lets go. */
    return () => {
      cancelled = true;
      observer?.disconnect();
      created?.remove();
      mapRef.current = null;
    };
  }, [token]);

  /* New points: replace the source's data in place — the layers and the view stay exactly as they are. */
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || map === null) return;
    /* ⚠️ Mid-reload (a new basemap) the source is gone and this does nothing: `style.load` re-adds it
       from `pointsRef`, which already holds these points. */
    map.getSource<GeoJSONSource>(POINTS_SOURCE_ID)?.setData(pointsData(points));
  }, [points, ready]);

  /*
   * Another basemap: reload the style. `style.load` above puts the points back on it — and clears a failed
   * style's overlay, so choosing another basemap is the way out of one.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (map === null || styledRef.current === null || styledRef.current === basemap) return;
    styledRef.current = basemap;
    styleLoadingRef.current = true;
    map.setStyle(BASEMAP_STYLE[basemap], RELOAD);
  }, [basemap]);

  /*
   * ⚠️ [LOCATION-INTEL.1] NEW COLOURS UNDER A BASEMAP THAT STAYS: REPAINT IN PLACE. At [LOCATION-INTEL] the basemap
   * always followed the theme, so every theme change reloaded the style and `style.load` repainted the dots; a basemap
   * the viewer CHOSE now survives a theme change, and without this the dots would keep the other mode's colours. Every
   * paint property `style.load` would add is set again — Mapbox skips an unchanged one — so the two paths cannot
   * disagree. Declared after the basemap effect, so a theme change that also reloads the style paints nothing twice.
   * ⚠️ THE GUARD IS OUR LAYER, NEVER `isStyleLoaded()`. Mid-reload the layers do not exist yet (`style.load` adds them
   * in `coloursRef`'s colours) and Mapbox's `setPaintProperty` THROWS on a style that has not loaded; `isStyleLoaded()`
   * also waits on tiles, so it would skip a repaint the style was ready for.
   */
  useEffect(() => {
    const map = mapRef.current;
    if (!ready || map === null) return;
    for (const layer of pointLayers(colours)) {
      if (!map.getLayer(layer.id)) continue;
      /* The cast says what `Object.entries` cannot: these keys are paint properties, read off the layer itself. */
      const paint = Object.entries(layer.paint ?? {}) as [
        keyof PaintSpecification,
        PaintSpecification[keyof PaintSpecification],
      ][];
      for (const [name, value] of paint) map.setPaintProperty(layer.id, name, value);
    }
  }, [colours, ready]);

  const centreOnCentralKolkata = (): void => {
    mapRef.current?.easeTo({ center: CENTRAL_KOLKATA, zoom: CENTRAL_KOLKATA_ZOOM, bearing: 0, pitch: 0 });
  };

  const showAllOfficers = (): void => {
    const bounds = boundsOf(points);
    if (bounds !== null) mapRef.current?.fitBounds(bounds, { padding: 64, maxZoom: 15 });
  };

  /* The failed style asked again — the one this map is showing. `style.load`, or another `error`, decides what next. */
  const retryStyle = (): void => {
    const map = mapRef.current;
    if (map === null) return;
    basemapSelectRef.current?.focus();
    styleLoadingRef.current = true;
    setFailure(null);
    map.setStyle(BASEMAP_STYLE[basemap], RELOAD);
  };

  /* A failure only a basemap can cure keeps the view controls; one no control can cure (no WebGL, no library,
     a refused token) leaves nothing on the map to press — and behind a failure overlay the map itself is `inert`. */
  const showControls = failure === null || failure === 'style';

  return (
    <div
      className="relative size-full overflow-hidden rounded-lg border border-border"
      data-testid="location-intel-map"
      data-ready={ready ? 'true' : 'false'}
    >
      {/*
        Our own controls, over the map's top-left corner — Mapbox's zoom and compass hold the top-right.
        ⚠️ FIRST IN THE SOURCE, because they are the first thing drawn: after the container, a keyboard reached them
        only past the canvas and every Mapbox control ([C5 §10a]'s rule: never a visual order the DOM disagrees with).
        ⚠️ A labelled `group`, never `toolbar`: that role promises one tab stop and arrow keys between the controls
        (the ARIA toolbar pattern), which three ordinary controls — a combobox among them, whose arrows are its own —
        do not implement.
        ⚠️ `max-w` keeps a narrow map's wrapped group clear of Mapbox's top-right buttons, which it covered on a phone.
        `z-30`, above the failure overlay, so a basemap that will not load can be swapped for one that will.
      */}
      {showControls && (
        <div
          className="absolute left-3 top-3 z-30 flex max-w-[calc(100%-4.5rem)] flex-wrap items-center gap-1.5 rounded-md border border-border bg-card p-1.5"
          role="group"
          aria-label={copy.viewControlsLabel}
          data-testid="location-intel-view-controls"
        >
          <Button
            size="small"
            icon={<LocateFixed className="size-3.5" aria-hidden />}
            title={copy.centralKolkataLabel}
            disabled={!ready}
            onClick={centreOnCentralKolkata}
            data-testid="location-intel-centre"
          >
            {copy.centralKolkata}
          </Button>
          <Button
            size="small"
            icon={<Expand className="size-3.5" aria-hidden />}
            title={copy.allOfficersLabel}
            disabled={!ready || points.length === 0}
            onClick={showAllOfficers}
            data-testid="location-intel-fit"
          >
            {copy.allOfficers}
          </Button>
          <Select<Basemap>
            ref={basemapSelectRef}
            size="small"
            aria-label={copy.basemapLabel}
            value={basemap}
            options={BASEMAP_OPTIONS}
            /* ⚠️ The value only: antd calls `onChange(value, option)`, and the option object is antd's, not ours.
               A failed style's overlay goes as the next style is asked for, so what happens next is what shows. */
            onChange={(next) => {
              setFailure((current) => (current === 'style' ? null : current));
              onBasemapChange(next);
            }}
            popupMatchSelectWidth={false}
            className="min-w-28"
            data-testid="location-intel-basemap"
          />
        </div>
      )}
      {/*
        ⚠️ THE MAPBOX CONTAINER: EMPTY (Mapbox requires no children, so every overlay is a sibling) AND SIZED IN
        NORMAL FLOW — `size-full`, never `absolute inset-0`, which `.mapboxgl-map`'s unlayered `position: relative`
        overrides to 0 px tall (the header above has the measurement).
      */}
      <div
        ref={containerRef}
        className="size-full"
        aria-label={copy.mapLabel}
        role="region"
        inert={failure !== null}
        data-testid="location-intel-map-container"
      />
      {/*
        [LOCATION-INTEL.1] One line for the states an empty-looking map must not hide: the points still being read,
        nobody to show, or a map that could not draw everybody. `aria-hidden`, because the page's visually hidden
        sentence beside the map already says the same words to a screen reader. `bottom-16` (64 px) keeps it above
        Mapbox's bottom-left stack — the logo, and the scale above it, whose top is 61 px up (measured) — which a
        narrow map's centred line otherwise covered. A 3 px margin: anything lower covers the scale again.
      */}
      {ready && failure === null && notice !== null && (
        <p
          className="pointer-events-none absolute bottom-16 left-1/2 z-10 w-max max-w-[calc(100%-2rem)] -translate-x-1/2 rounded-md border border-border bg-card px-3 py-1.5 text-center text-sm"
          aria-hidden
          data-testid="location-intel-map-notice"
        >
          {notice}
        </p>
      )}
      {failure !== null ? (
        <div
          className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-1 bg-card p-6 text-center"
          role="alert"
          data-testid="location-intel-map-failed"
        >
          <p className="font-medium">{copy.mapFailedTitle}</p>
          <p className="max-w-prose text-sm text-muted-foreground">{FAILURE_WORDS[failure]}</p>
          {failure === 'style' && (
            <Button className="mt-3" onClick={retryStyle} data-testid="location-intel-map-retry">
              {copy.retry}
            </Button>
          )}
        </div>
      ) : (
        !ready && (
          <div
            className="pointer-events-none absolute inset-0 flex items-center justify-center bg-card/60 text-sm text-muted-foreground"
            role="status"
          >
            {copy.loading}
          </div>
        )
      )}
    </div>
  );
};
